export const PREVIEW_POLL_INTERVAL_MS = 2500;
export const PREVIEW_POLL_MAX_ATTEMPTS = 240;
export const PREVIEW_POLL_MAX_TRANSIENT_FAILURES = 3;

// A one-shot timer per tick, so specs can drive the poll loop with fake timers
// one tick at a time.
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function progressFrom(data) {
  return {
    processed: data.processed ?? 0,
    total: data.total ?? 0,
    estimatedSecondsRemaining: data.estimated_seconds_remaining ?? null,
  };
}

/**
 * Polls a background preview's status endpoint until it settles and returns the
 * outcome:
 *   - `{ outcome: "ready", data }` for a `ready` status, `data` being the full payload
 *   - `{ outcome: "stale" }` as soon as `isCurrent()` reports the poll superseded
 *   - `{ outcome: "cancelled" | "expired", errors }` for those statuses
 *   - `{ outcome: "failed", errors }` for `failed`, an unrecognized status, or the
 *     attempt cap
 *
 * A `running` status calls `onProgress({ processed, total, estimatedSecondsRemaining })`
 * and resets the attempt counter, so the cap only counts consecutive `pending` ticks.
 * A rejected `fetchStatus` is retried; the `maxTransientFailures`-th consecutive
 * rejection is rethrown.
 *
 * @param {Object} options
 * @param {() => Promise<Object>} options.fetchStatus - Fetches the current status payload.
 * @param {() => boolean} [options.isCurrent] - Whether this poll still owns the store state.
 * @param {(progress: Object) => void} [options.onProgress] - Receives each `running` progress report.
 * @param {number} [options.intervalMs]
 * @param {number} [options.maxAttempts]
 * @param {number} [options.maxTransientFailures]
 * @returns {Promise<{ outcome: string, data?: Object, errors?: string[] }>}
 */
export async function pollPreviewStatus({
  fetchStatus,
  isCurrent = () => true,
  onProgress = () => {},
  intervalMs = PREVIEW_POLL_INTERVAL_MS,
  maxAttempts = PREVIEW_POLL_MAX_ATTEMPTS,
  maxTransientFailures = PREVIEW_POLL_MAX_TRANSIENT_FAILURES,
}) {
  let attempt = 0;
  let transientFailures = 0;

  while (attempt < maxAttempts) {
    attempt += 1;
    await sleep(intervalMs);

    if (!isCurrent()) {
      return { outcome: "stale" };
    }

    let data;
    try {
      data = await fetchStatus();
    } catch (error) {
      if (!isCurrent()) {
        return { outcome: "stale" };
      }

      transientFailures += 1;
      if (transientFailures >= maxTransientFailures) {
        throw error;
      }

      continue;
    }

    transientFailures = 0;

    if (!isCurrent()) {
      return { outcome: "stale" };
    }

    switch (data?.status) {
      case "ready":
        return { outcome: "ready", data };
      case "running":
        attempt = 0;
        onProgress(progressFrom(data));
        continue;
      case "pending":
        continue;
      case "cancelled":
      case "expired":
        return { outcome: data.status, errors: data.errors ?? [] };
      default:
        return { outcome: "failed", errors: data?.errors ?? [] };
    }
  }

  return { outcome: "failed", errors: [] };
}
